import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Resolve a backend-relative media path (e.g. /api/media/x.avif) to an absolute URL
export const mediaUrl = (p) => (p && typeof p === "string" && p.startsWith("/") ? `${BACKEND_URL}${p}` : p || "");

const TOKEN_KEY = "gf_token";
export const getToken = () => localStorage.getItem(TOKEN_KEY);
export const setToken = (t) => localStorage.setItem(TOKEN_KEY, t);
export const clearToken = () => localStorage.removeItem(TOKEN_KEY);

export const api = axios.create({
  baseURL: API,
  headers: { "Content-Type": "application/json" },
});

// Authorized instance for CRM endpoints
export const authApi = axios.create({
  baseURL: API,
  headers: { "Content-Type": "application/json" },
});
authApi.interceptors.request.use((cfg) => {
  const t = getToken();
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

// ---- Public ----
export async function createLead(payload) {
  const { data } = await api.post("/leads", payload);
  return data;
}

// ---- Auth ----
export async function login(email, password) {
  const { data } = await api.post("/auth/login", { email, password });
  return data;
}
export async function register({ name, email, password, invite_code, role }) {
  const { data } = await api.post("/auth/register", {
    name,
    email,
    password,
    invite_code,
    ...(role ? { role } : {}),
  });
  return data;
}

// ---- 2FA ----
export async function verifyLogin(mfa_token, code) {
  const { data } = await api.post("/auth/2fa/verify-login", { mfa_token, code });
  return data;
}
export async function twofaSetup() {
  const { data } = await authApi.post("/auth/2fa/setup");
  return data;
}
export async function twofaEnable(code) {
  const { data } = await authApi.post("/auth/2fa/enable", { code });
  return data;
}
export async function twofaDisable(code) {
  const { data } = await authApi.post("/auth/2fa/disable", { code });
  return data;
}
export async function twofaEmailSend() {
  const { data } = await authApi.post("/auth/2fa/email/send");
  return data;
}
export async function twofaEmailEnable(code) {
  const { data } = await authApi.post("/auth/2fa/email/enable", { code });
  return data;
}

// ---- OAuth (Google / LinkedIn) ----
export async function fetchProviders() {
  const { data } = await api.get("/auth/providers");
  return data;
}
export async function oauthExchange(provider, code, redirect_uri) {
  const { data } = await api.post(`/auth/oauth/${provider}`, { code, redirect_uri });
  return data;
}
// Redirect URI must be derived from the current origin (never hardcoded / env)
export function oauthRedirectUri(provider) {
  return `${window.location.origin}/auth/${provider}`;
}
export function startOAuth(provider, cfg) {
  const state = Math.random().toString(36).slice(2) + Date.now().toString(36);
  sessionStorage.setItem(`oauth_state_${provider}`, state);
  const redirect = oauthRedirectUri(provider);
  const params = new URLSearchParams({
    response_type: "code",
    client_id: cfg.client_id,
    redirect_uri: redirect,
    scope: cfg.scope,
    state,
  });
  if (provider === "google") {
    params.set("access_type", "offline");
    params.set("prompt", "consent");
  }
  window.location.href = `${cfg.authorize_url}?${params.toString()}`;
}
export async function fetchMe() {
  const { data } = await authApi.get("/auth/me");
  return data;
}

// ---- Content (mini-CMS) ----
export async function getContent() {
  const { data } = await api.get("/content");
  return data;
}
export async function updateContent(payload) {
  const { data } = await authApi.put("/content", payload);
  return data;
}

// ---- Passkey / WebAuthn ----
export async function passkeyAvailable() {
  try {
    const { data } = await api.get("/auth/passkey/available");
    return !!data?.available;
  } catch {
    return false;
  }
}
export async function passkeyRegisterOptions() {
  const { data } = await authApi.post("/auth/passkey/register/options");
  return data;
}
export async function passkeyRegisterVerify(credential, name) {
  const { data } = await authApi.post("/auth/passkey/register/verify", { credential, name });
  return data;
}
export async function passkeyList() {
  const { data } = await authApi.get("/auth/passkey/list");
  return data;
}
export async function passkeyDelete(id) {
  const { data } = await authApi.delete(`/auth/passkey/${encodeURIComponent(id)}`);
  return data;
}
export async function passkeyLoginOptions(email) {
  const { data } = await api.post("/auth/passkey/login/options", { email });
  return data;
}
export async function passkeyLoginVerify(email, credential) {
  const { data } = await api.post("/auth/passkey/login/verify", { email, credential });
  return data;
}

// ---- CRM ----
export async function fetchLeads(params = {}) {
  const { data } = await authApi.get("/leads", { params });
  return data;
}
export async function fetchStats() {
  const { data } = await authApi.get("/leads/stats");
  return data;
}
export async function updateLeadStatus(id, status) {
  const { data } = await authApi.patch(`/leads/${id}`, { status });
  return data;
}
export async function addLeadNote(id, text) {
  const { data } = await authApi.post(`/leads/${id}/notes`, { text });
  return data;
}
export async function deleteLead(id) {
  const { data } = await authApi.delete(`/leads/${id}`);
  return data;
}


// ---- Contacts ----
export async function listContacts(q) {
  const { data } = await authApi.get("/contacts", { params: q ? { q } : {} });
  return data;
}
export async function createContact(payload) {
  const { data } = await authApi.post("/contacts", payload);
  return data;
}
export async function updateContact(id, payload) {
  const { data } = await authApi.patch(`/contacts/${id}`, payload);
  return data;
}
export async function deleteContact(id) {
  const { data } = await authApi.delete(`/contacts/${id}`);
  return data;
}

// ---- Settings ----
export async function getSettings() {
  const { data } = await authApi.get("/settings");
  return data;
}
export async function updateSettings(payload) {
  const { data } = await authApi.put("/settings", payload);
  return data;
}

// ---- Quotes ----
export async function listQuotes() {
  const { data } = await authApi.get("/quotes");
  return data;
}
export async function createQuote(payload) {
  const { data } = await authApi.post("/quotes", payload);
  return data;
}
export async function updateQuote(id, payload) {
  const { data } = await authApi.patch(`/quotes/${id}`, payload);
  return data;
}
export async function sendQuote(id) {
  const { data } = await authApi.post(`/quotes/${id}/send`);
  return data;
}
export async function deleteQuote(id) {
  const { data } = await authApi.delete(`/quotes/${id}`);
  return data;
}

// ---- Contracts ----
export async function listContracts() {
  const { data } = await authApi.get("/contracts");
  return data;
}
export async function createContract(payload) {
  const { data } = await authApi.post("/contracts", payload);
  return data;
}
export async function updateContractDoc(id, payload) {
  const { data } = await authApi.patch(`/contracts/${id}`, payload);
  return data;
}
export async function sendContract(id) {
  const { data } = await authApi.post(`/contracts/${id}/send`);
  return data;
}
export async function deleteContract(id) {
  const { data } = await authApi.delete(`/contracts/${id}`);
  return data;
}

// Open a stored PDF (auth) in a new tab
export async function openDocPdf(kind, id) {
  const path = kind === "quote" ? `/quotes/${id}/pdf` : `/contracts/${id}/pdf`;
  const res = await authApi.get(path, { responseType: "blob" });
  const url = URL.createObjectURL(res.data);
  window.open(url, "_blank");
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}

// ---- Public signing (no auth) ----
export async function publicGetDoc(kind, token) {
  const { data } = await api.get(`/public/${kind}/${token}`);
  return data;
}
export async function publicRequestOtp(kind, token) {
  const { data } = await api.post(`/public/${kind}/${token}/otp`);
  return data;
}
export async function publicSignDoc(kind, token, payload) {
  const { data } = await api.post(`/public/${kind}/${token}/sign`, payload);
  return data;
}

// ---- Bookings (public) ----
export async function getBookingConfig() {
  const { data } = await api.get("/booking/config");
  return data;
}
export async function bookingCheckout(payload) {
  const { data } = await api.post("/booking/checkout", payload);
  return data;
}
export async function paymentStatus(sessionId) {
  const { data } = await api.get(`/payments/status/${sessionId}`);
  return data;
}
// ---- Bookings (CRM) ----
export async function listBookings() {
  const { data } = await authApi.get("/bookings");
  return data;
}
export async function deleteBooking(id) {
  const { data } = await authApi.delete(`/bookings/${id}`);
  return data;
}
// ---- Client area (public) ----
export async function clientOtp(email) {
  const { data } = await api.post("/client/otp", { email });
  return data;
}
export async function clientData(email, code) {
  const { data } = await api.post("/client/data", { email, code });
  return data;
}
export async function clientCancelBooking(clientToken, bookingId, reason = "") {
  const { data } = await api.post(
    `/client/bookings/${bookingId}/cancel`,
    { reason },
    { headers: { Authorization: `Bearer ${clientToken}` } }
  );
  return data;
}
export async function clientRescheduleBooking(clientToken, bookingId, newSlotStart) {
  const { data } = await api.post(
    `/client/bookings/${bookingId}/reschedule`,
    { new_slot_start: newSlotStart },
    { headers: { Authorization: `Bearer ${clientToken}` } }
  );
  return data;
}

// ---- Blog (public) ----
export async function fetchBlogPosts(params = {}) {
  const { data } = await api.get("/blog/posts", { params });
  return data;
}
export async function fetchBlogCategories() {
  const { data } = await api.get("/blog/categories");
  return data;
}
export async function fetchBlogPost(slug) {
  const { data } = await api.get(`/blog/posts/${slug}`);
  return data;
}
// ---- Blog (CRM, auth) ----
export async function adminListPosts() {
  const { data } = await authApi.get("/blog/admin/posts");
  return data;
}
export async function adminGetPost(id) {
  const { data } = await authApi.get(`/blog/admin/posts/${id}`);
  return data;
}
export async function createPost(payload) {
  const { data } = await authApi.post("/blog/posts", payload);
  return data;
}
export async function updatePost(id, payload) {
  const { data } = await authApi.patch(`/blog/posts/${id}`, payload);
  return data;
}
export async function deletePost(id) {
  const { data } = await authApi.delete(`/blog/posts/${id}`);
  return data;
}
export async function generateBlogCovers(overwrite = false) {
  const { data } = await authApi.post(`/blog/generate-covers?overwrite=${overwrite}`);
  return data;
}

// ---- Media gallery (CRM, auth) ----
export async function gallerySearch(source, q, page = 1) {
  const { data } = await authApi.get(`/gallery/search/${source}`, { params: { q, page } });
  return data;
}
export async function galleryImport(payload) {
  const { data } = await authApi.post("/gallery/import", payload);
  return data;
}
export async function galleryUpload(file) {
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await authApi.post("/gallery/upload", fd, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data;
}
export async function galleryList() {
  const { data } = await authApi.get("/gallery");
  return data;
}
export async function galleryDelete(id) {
  const { data } = await authApi.delete(`/gallery/${id}`);
  return data;
}

// ---- Google Calendar (CRM, auth) ----
export async function googleStatus() {
  const { data } = await authApi.get("/google/status");
  return data;
}
// Redirect URI must be derived from the current origin (never hardcoded / env)
export function googleRedirectUri() {
  return `${window.location.origin}/google-calendar/callback`;
}
export async function googleAuthUrl() {
  const { data } = await authApi.get("/google/auth-url", {
    params: { redirect_uri: googleRedirectUri() },
  });
  return data;
}
export async function googleExchange(code) {
  const { data } = await authApi.post("/google/exchange", {
    code,
    redirect_uri: googleRedirectUri(),
  });
  return data;
}
export async function googleDisconnect() {
  const { data } = await authApi.post("/google/disconnect");
  return data;
}

